import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import EmployeeProfileSection from "../components/EmployeeProfileSection";

export default function Profile({ user }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Chunk 16D — local copy of MFA-enabled state so we can update the UI
  // immediately after enroll/disable without forcing a full session reload.
  // Initialized from the user prop; refreshed via /me after each transition.
  const [totpEnabled, setTotpEnabled] = useState(Boolean(user.totp_enabled));
  const [mfaPhase, setMfaPhase] = useState("idle");   // "idle" | "enrolling" | "disabling"
  const [mfaEnrollData, setMfaEnrollData] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPassword, setMfaPassword] = useState("");
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaMsg, setMfaMsg] = useState(null);

  // After any enroll/disable, re-fetch the user record so totp_enabled
  // reflects authoritative state (in case the page is reloaded later).
  const refreshSelf = async () => {
    try {
      const me = await apiFetch("/me");
      setTotpEnabled(Boolean(me.totp_enabled));
      localStorage.setItem("user", JSON.stringify(me));
    } catch {}
  };

  useEffect(() => { setTotpEnabled(Boolean(user.totp_enabled)); }, [user.totp_enabled]);

  // mig 125 — self-service email signature fields.
  const [sig, setSig] = useState({
    title: user.title || "", phone_direct: user.phone_direct || "",
    phone_office: user.phone_office || "", phone_mobile: user.phone_mobile || "",
    signature_address: user.signature_address || "",
  });
  const [sigSaving, setSigSaving] = useState(false);
  const [sigMsg, setSigMsg] = useState(null);

  // mig 126 — Outlook connection status.
  const [outlook, setOutlook] = useState(null);
  const [outlookBusy, setOutlookBusy] = useState(false);
  const loadOutlook = async () => {
    try { setOutlook(await apiFetch("/me/outlook/status")); } catch { setOutlook({ connected: false }); }
  };
  useEffect(() => { loadOutlook(); }, []);
  const connectOutlook = async () => {
    setOutlookBusy(true);
    try { const { url } = await apiFetch("/me/outlook/connect"); window.location.href = url; }
    catch (e) { let d="Could not start sign-in"; try{d=JSON.parse(e.message).detail||d;}catch{}; setOutlook({...(outlook||{}), last_error:d}); setOutlookBusy(false); }
  };
  const disconnectOutlook = async () => {
    if (!window.confirm("Disconnect your Outlook account from Atticus? You'll need to reconnect to send email.")) return;
    setOutlookBusy(true);
    try { await apiFetch("/me/outlook/disconnect", { method:"POST", body:{} }); await loadOutlook(); }
    catch {} setOutlookBusy(false);
  };
  const saveSignature = async () => {
    setSigMsg(null); setSigSaving(true);
    try {
      const me = await apiFetch("/me/signature", { method: "PATCH", body: sig });
      localStorage.setItem("user", JSON.stringify(me));
      setSigMsg({ type: "success", text: "Signature saved" });
    } catch (e) {
      let d = "Failed to save signature"; try { d = JSON.parse(e.message).detail || d; } catch {}
      setSigMsg({ type: "error", text: d });
    }
    setSigSaving(false);
  };

  const changePassword = async () => {
    setMsg(null);
    if (!pw.current || !pw.next || !pw.confirm) { setMsg({type:"error",text:"All fields are required"}); return; }
    if (pw.next !== pw.confirm) { setMsg({type:"error",text:"New passwords do not match"}); return; }
    if (pw.next.length < 12) { setMsg({type:"error",text:"New password must be at least 12 characters"}); return; }
    if (pw.next === pw.current) { setMsg({type:"error",text:"New password must differ from current password"}); return; }
    setSaving(true);
    try {
      await apiFetch("/me/password", { method:"POST", body: { current_password: pw.current, new_password: pw.next } });
      setMsg({type:"success",text:"Password updated successfully"});
      setPw({ current:"", next:"", confirm:"" });
    } catch(e) {
      let detail = "Failed to change password";
      try { detail = JSON.parse(e.message).detail || detail; } catch {}
      setMsg({type:"error",text: detail});
    }
    setSaving(false);
  };

  // -------------------- Chunk 16D — MFA flows --------------------

  const beginEnroll = async () => {
    setMfaMsg(null); setMfaSaving(true);
    try {
      const data = await apiFetch("/me/mfa/enroll", { method:"POST", body: {} });
      setMfaEnrollData(data);
      setMfaCode("");
      setMfaPhase("enrolling");
    } catch (e) {
      let detail = "Could not start MFA enrollment";
      try { detail = JSON.parse(e.message).detail || detail; } catch {}
      setMfaMsg({type:"error", text: detail});
    } finally { setMfaSaving(false); }
  };

  const confirmEnroll = async () => {
    setMfaMsg(null); setMfaSaving(true);
    try {
      await apiFetch("/me/mfa/verify-enrollment", { method:"POST", body: { code: mfaCode.trim() } });
      setMfaMsg({type:"success", text:"Two-factor authentication is now enabled. You'll be asked for a code each time you sign in."});
      setMfaPhase("idle");
      setMfaEnrollData(null);
      setMfaCode("");
      await refreshSelf();
    } catch (e) {
      let detail = "Verification failed";
      try { detail = JSON.parse(e.message).detail || detail; } catch {}
      setMfaMsg({type:"error", text: detail});
    } finally { setMfaSaving(false); }
  };

  const cancelEnroll = () => {
    setMfaPhase("idle");
    setMfaEnrollData(null);
    setMfaCode("");
    setMfaMsg(null);
  };

  const disable = async () => {
    setMfaMsg(null); setMfaSaving(true);
    try {
      await apiFetch("/me/mfa/disable", { method:"POST", body: { password: mfaPassword, code: mfaCode.trim() } });
      setMfaMsg({type:"success", text:"Two-factor authentication disabled."});
      setMfaPhase("idle");
      setMfaPassword(""); setMfaCode("");
      await refreshSelf();
    } catch (e) {
      let detail = "Failed to disable MFA";
      try { detail = JSON.parse(e.message).detail || detail; } catch {}
      setMfaMsg({type:"error", text: detail});
    } finally { setMfaSaving(false); }
  };

  // ----------------------------- styling helpers ---------------------------
  const successStyle = { background:"#d1fae5", border:"1px solid #6ee7b7", color:"#065f46", padding:"8px 12px", borderRadius:4, fontSize:12, marginBottom:14 };
  const readField = { padding:"6px 9px", fontSize:13, background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text2)" };

  return (
    <div className="page">
      <div className="section-header"><span className="section-title">My Profile</span></div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header"><h3>Account Information</h3></div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Username</label>
                <div style={{...readField, fontFamily:"JetBrains Mono,monospace"}}>{user.username}</div>
              </div>
              <div className="form-group">
                <label>Role</label>
                <div style={{...readField, textTransform:"capitalize"}}>{user.role}</div>
              </div>
              <div className="form-group full">
                <label>Full Name</label>
                <div style={readField}>{user.full_name || "—"}</div>
              </div>
              <div className="form-group full">
                <label>Email</label>
                <div style={readField}>{user.email || "—"}</div>
              </div>
            </div>
            <div style={{marginTop:14, fontSize:11, color:"var(--text3)", fontStyle:"italic"}}>
              To update your name, email, or role, contact an administrator.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Change Password</h3></div>
          <div className="card-body">
            {msg && <div className={msg.type==="error"?"alert-error":""} style={msg.type==="success"?successStyle:{}}>{msg.text}</div>}
            <div className="form-group" style={{marginBottom:10}}>
              <label>Current Password</label>
              <input type="password" value={pw.current} onChange={e=>setPw({...pw,current:e.target.value})} autoComplete="current-password"/>
            </div>
            <div className="form-group" style={{marginBottom:10}}>
              <label>New Password</label>
              <input type="password" value={pw.next} onChange={e=>setPw({...pw,next:e.target.value})} autoComplete="new-password"/>
            </div>
            <div className="form-group" style={{marginBottom:10}}>
              <label>Confirm New Password</label>
              <input type="password" value={pw.confirm} onChange={e=>setPw({...pw,confirm:e.target.value})} autoComplete="new-password"/>
            </div>
            <div style={{display:"flex", justifyContent:"flex-end", marginTop:14}}>
              <button className="btn btn-primary" onClick={changePassword} disabled={saving}>{saving?"Saving...":"Change Password"}</button>
            </div>
            <div style={{marginTop:10, fontSize:11, color:"var(--text3)"}}>
              Minimum 12 characters. New password must differ from current password.
            </div>
          </div>
        </div>
      </div>

      {/* Chunk 16D — MFA card spans full width below the two-up grid */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>Two-Factor Authentication</h3></div>
        <div className="card-body">
          {mfaMsg && <div className={mfaMsg.type==="error"?"alert-error":""} style={mfaMsg.type==="success"?successStyle:{}}>{mfaMsg.text}</div>}

          {mfaPhase === "idle" && totpEnabled && (
            <>
              <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:14}}>
                <span style={{padding:"3px 10px", borderRadius:4, fontSize:11, fontWeight:700, background:"#d1fae5", color:"#065f46", letterSpacing:0.5}}>ENABLED</span>
                <span style={{fontSize:13, color:"var(--text2)"}}>You'll be asked for a 6-digit code from your authenticator each time you sign in.</span>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => { setMfaPhase("disabling"); setMfaMsg(null); }}>Disable two-factor authentication</button>
            </>
          )}

          {mfaPhase === "idle" && !totpEnabled && (
            <>
              <div style={{fontSize:13, color:"var(--text2)", marginBottom:14, lineHeight:1.5}}>
                Two-factor authentication adds a second step at sign-in: a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password, etc.).
                If your password is ever exposed, your account stays protected.
              </div>
              <button className="btn btn-primary btn-sm" onClick={beginEnroll} disabled={mfaSaving}>{mfaSaving?"Starting...":"Enroll in two-factor authentication"}</button>
            </>
          )}

          {mfaPhase === "enrolling" && mfaEnrollData && (
            <div style={{display:"grid", gridTemplateColumns:"auto 1fr", gap:24, alignItems:"start"}}>
              <div style={{textAlign:"center"}}>
                <img src={mfaEnrollData.qr_png} alt="MFA QR code" style={{width:200, height:200, border:"1px solid var(--border)", borderRadius:6, background:"#fff", padding:8}}/>
                <div style={{fontSize:11, color:"var(--text3)", marginTop:6}}>Scan with your authenticator app</div>
              </div>
              <div>
                <div style={{fontSize:13, color:"var(--text2)", marginBottom:10}}>
                  <strong>1.</strong> Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and add a new account by scanning the QR code.
                </div>
                <div style={{fontSize:13, color:"var(--text2)", marginBottom:14}}>
                  Can't scan? Enter this secret manually:
                  <div style={{fontFamily:"JetBrains Mono,monospace", fontSize:12, background:"var(--bg3)", padding:"6px 10px", borderRadius:4, marginTop:6, wordBreak:"break-all"}}>{mfaEnrollData.secret}</div>
                </div>
                <div style={{fontSize:13, color:"var(--text2)", marginBottom:6}}>
                  <strong>2.</strong> Enter the 6-digit code your app shows now to confirm:
                </div>
                <div className="form-group" style={{marginBottom:14}}>
                  <input
                    value={mfaCode}
                    onChange={e=>setMfaCode(e.target.value)}
                    placeholder="123 456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={10}
                    style={{ letterSpacing:2, fontFamily:"JetBrains Mono,monospace", fontSize:16, textAlign:"center", maxWidth:200 }}
                  />
                </div>
                <div style={{display:"flex", gap:8}}>
                  <button className="btn btn-primary btn-sm" onClick={confirmEnroll} disabled={mfaSaving || mfaCode.trim().length < 6}>{mfaSaving?"Verifying...":"Confirm and enable"}</button>
                  <button className="btn btn-outline btn-sm" onClick={cancelEnroll} disabled={mfaSaving}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {mfaPhase === "disabling" && (
            <>
              <div style={{fontSize:13, color:"var(--text2)", marginBottom:14}}>
                To disable two-factor authentication, confirm both your password AND a current code from your authenticator.
                This prevents anyone with just your browser session from removing your second factor.
              </div>
              <div className="form-group" style={{marginBottom:10, maxWidth:300}}>
                <label>Current Password</label>
                <input type="password" value={mfaPassword} onChange={e=>setMfaPassword(e.target.value)} autoComplete="current-password"/>
              </div>
              <div className="form-group" style={{marginBottom:14, maxWidth:300}}>
                <label>Authentication code</label>
                <input
                  value={mfaCode}
                  onChange={e=>setMfaCode(e.target.value)}
                  placeholder="123 456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={10}
                  style={{ letterSpacing:2, fontFamily:"JetBrains Mono,monospace", fontSize:16, textAlign:"center" }}
                />
              </div>
              <div style={{display:"flex", gap:8}}>
                <button className="btn btn-red btn-sm" onClick={disable} disabled={mfaSaving || !mfaPassword || mfaCode.trim().length < 6}>{mfaSaving?"Disabling...":"Disable MFA"}</button>
                <button className="btn btn-outline btn-sm" onClick={() => { setMfaPhase("idle"); setMfaPassword(""); setMfaCode(""); setMfaMsg(null); }} disabled={mfaSaving}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* mig 126 — Outlook / Microsoft connection. Sign-in happens on Microsoft's
          own page; Atticus never sees your password. */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>Outlook email</h3></div>
        <div className="card-body">
          {!outlook ? <div style={{fontSize:13, color:"var(--text3)"}}>Loading…</div> : outlook.connected ? (
            <>
              <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap"}}>
                <span style={{padding:"3px 10px", borderRadius:4, fontSize:11, fontWeight:700, background:"#d1fae5", color:"#065f46", letterSpacing:0.5}}>CONNECTED</span>
                <span style={{fontSize:13, color:"var(--text2)"}}>{outlook.email || "Microsoft account"}</span>
                <span style={{padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:600, background: outlook.email_send_enabled ? "#dbeafe" : "var(--bg3)", color: outlook.email_send_enabled ? "#1e40af" : "var(--text3)"}}>
                  {outlook.email_send_enabled ? "Sending enabled" : "Sending disabled — ask an admin"}
                </span>
              </div>
              {outlook.last_error && <div className="alert-error" style={{marginBottom:10}}>{outlook.last_error}</div>}
              <button className="btn btn-outline btn-sm" onClick={disconnectOutlook} disabled={outlookBusy}>{outlookBusy?"Working…":"Disconnect Outlook"}</button>
            </>
          ) : (
            <>
              <div style={{fontSize:13, color:"var(--text2)", marginBottom:14, lineHeight:1.5}}>
                Connect your Microsoft 365 account to send email from Atticus. You'll sign in on Microsoft's own page — Atticus never sees or stores your password, only a secure token you can revoke here anytime.
              </div>
              {outlook.configured === false && <div className="alert-error" style={{marginBottom:10}}>Microsoft integration isn't configured on the server yet.</div>}
              {outlook.last_error && <div className="alert-error" style={{marginBottom:10}}>{outlook.last_error}</div>}
              <button className="btn btn-primary btn-sm" onClick={connectOutlook} disabled={outlookBusy || outlook.configured === false}>{outlookBusy?"Redirecting…":"Connect Outlook"}</button>
              {!outlook.email_send_enabled && <div style={{marginTop:10, fontSize:11, color:"var(--text3)"}}>Note: an administrator must also enable email sending on your account before you can send.</div>}
            </>
          )}
        </div>
      </div>

      {/* mig 125 — self-service email signature. Name/company/logo/disclaimer come
          from your account + firm branding; these fields personalize the template. */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>Email signature</h3></div>
        <div className="card-body">
          {sigMsg && <div className={sigMsg.type==="error"?"alert-error":""} style={sigMsg.type==="success"?successStyle:{}}>{sigMsg.text}</div>}
          <div style={{fontSize:13, color:"var(--text2)", marginBottom:14, lineHeight:1.5}}>
            These fields personalize your signature on email sent from Atticus. Your name, email, the company name, logo, and confidentiality notice come from your account and firm branding. Blank phone lines are hidden.
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, alignItems:"start"}}>
            <div>
              <div className="form-group" style={{marginBottom:10}}><label>Title</label>
                <input value={sig.title} placeholder="e.g. Claims Manager" onChange={e=>setSig({...sig,title:e.target.value})}/></div>
              <div className="form-group" style={{marginBottom:10}}><label>Direct phone</label>
                <input value={sig.phone_direct} onChange={e=>setSig({...sig,phone_direct:e.target.value})}/></div>
              <div className="form-group" style={{marginBottom:10}}><label>Office phone</label>
                <input value={sig.phone_office} onChange={e=>setSig({...sig,phone_office:e.target.value})}/></div>
              <div className="form-group" style={{marginBottom:10}}><label>Mobile phone</label>
                <input value={sig.phone_mobile} onChange={e=>setSig({...sig,phone_mobile:e.target.value})}/></div>
              <div className="form-group" style={{marginBottom:10}}><label>Address</label>
                <input value={sig.signature_address} placeholder="4017 13th Ave W, Seattle, WA 98119 (firm default)" onChange={e=>setSig({...sig,signature_address:e.target.value})}/></div>
              <div style={{display:"flex", justifyContent:"flex-end", marginTop:6}}>
                <button className="btn btn-primary" onClick={saveSignature} disabled={sigSaving}>{sigSaving?"Saving...":"Save signature"}</button>
              </div>
            </div>
            <div>
              <div style={{fontSize:11, color:"var(--text3)", marginBottom:6, textTransform:"uppercase", letterSpacing:0.5}}>Preview</div>
              <SignaturePreview user={user} sig={sig}/>
            </div>
          </div>
        </div>
      </div>

      {/* EMPLOYEE-PROFILE-1: headshot / bio / specialties / credentials / promote-to-website */}
      <EmployeeProfileSection user={user} />
    </div>
  );
}

// mig 125 — live signature preview, mirrors the firm HTML template. The firm logo
// (embedded inline at send time) is shown here as a placeholder box.
const FIRM = {
  company: "Walters Northwest Claims Services, Inc.",
  website: "waltersnorthwest.com",
  address: "4017 13th Ave W, Seattle, WA 98119",
  navy: "#000066", teal: "#2C8EBA",
  disclaimer: "This message (including all attachment(s)) contains proprietary, confidential, and/or privileged material intended only for the person or entity to which it is addressed. If you received this communication in error, any use, review, retransmission, distribution, reproduction, or action taken in reliance upon this material is strictly prohibited; please contact the sender and destroy (including attachments).",
};
function SignaturePreview({ user, sig }) {
  const addr = (sig.signature_address || "").trim() || FIRM.address;
  const line = (label, val) => val && val.trim()
    ? <div style={{fontSize:12, color:"#333"}}><span style={{color:FIRM.teal, fontWeight:600, marginRight:5}}>{label}</span>{val}</div>
    : null;
  return (
    <div style={{background:"#fff", border:"1px solid #e5e5e5", padding:"14px 16px", fontFamily:"Arial,Helvetica,sans-serif", maxWidth:460}}>
      {/* logo vertically centered against the contact block; teal rule wraps only that block */}
      <div style={{display:"flex", gap:14, alignItems:"center"}}>
        <div style={{flex:"0 0 auto", width:72, height:72, border:`1px solid ${FIRM.navy}`, borderRadius:6, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:FIRM.navy}}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={FIRM.navy} strokeWidth="1.6"><circle cx="12" cy="12" r="9"/><polygon points="15,9 12.5,12.5 9,15 11.5,11.5" fill={FIRM.navy} stroke="none"/></svg>
          <span style={{fontSize:8, marginTop:2}}>logo</span>
        </div>
        <div style={{minWidth:0, borderLeft:`3px solid ${FIRM.teal}`, paddingLeft:14}}>
          <div style={{fontSize:15, fontWeight:700, color:FIRM.navy, lineHeight:1.2}}>{user.full_name || user.username}</div>
          {sig.title && sig.title.trim() && <div style={{fontSize:12, color:FIRM.teal, fontWeight:700, marginTop:1}}>{sig.title}</div>}
          <div style={{fontSize:12, color:FIRM.navy, marginTop:1}}>{FIRM.company}</div>
          <div style={{borderTop:`1px solid ${FIRM.teal}`, margin:"7px 0", width:200}}/>
          {line("D", sig.phone_direct)}
          {line("O", sig.phone_office)}
          {line("M", sig.phone_mobile)}
          {user.email && <div style={{fontSize:12, color:"#333"}}>{user.email}</div>}
          <div style={{fontSize:12, color:"#333"}}>{FIRM.website}</div>
          <div style={{fontSize:12, color:"#333"}}>{addr}</div>
        </div>
      </div>
      {/* disclaimer: full width, left-justified to the far left (under the logo) */}
      <div style={{fontSize:10, color:"#888", marginTop:8, lineHeight:1.4}}>{FIRM.disclaimer}</div>
    </div>
  );
}
